import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, BarChart3, Wallet as WalletIcon } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

export function PortfolioTab({ portfolio, onSwitchTab }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6" data-testid="portfolio-section">
      {/* Net Worth Hero */}
      <div className="bg-[#090B0F] border border-white/10 p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-2">{t('wallet.netWorth')}</p>
            <div className="text-5xl font-black tracking-tight" style={{ fontFamily: 'Chivo' }} data-testid="portfolio-net-worth">
              <span className="text-white">${portfolio.net_worth?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex items-center gap-4 mt-3">
              <span className={`text-lg font-bold font-mono ${portfolio.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {portfolio.total_pnl >= 0 ? '+' : ''}${portfolio.total_pnl?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
              <span className={`px-2 py-0.5 text-sm font-mono font-bold ${portfolio.overall_return_pct >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                {portfolio.overall_return_pct >= 0 ? '+' : ''}{portfolio.overall_return_pct?.toFixed(2)}% ROI
              </span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 text-right">
            <div>
              <p className="text-xs text-slate-500 font-mono uppercase">{t('wallet.walletLabel')}</p>
              <p className="text-lg font-bold text-white">${portfolio.wallet_balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 font-mono uppercase">{t('wallet.inAgents')}</p>
              <p className="text-lg font-bold text-white">${portfolio.total_invested_in_agents?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 font-mono uppercase">{t('wallet.portfolioValue')}</p>
              <p className="text-lg font-bold text-emerald-400">${portfolio.portfolio_value?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Evolution Chart */}
      {portfolio.evolution?.length > 1 && (
        <div className="bg-[#090B0F] border border-white/10 p-6">
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-500" /> Capital Evolution
          </h3>
          <div className="h-[280px]" data-testid="portfolio-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={portfolio.evolution}>
                <defs>
                  <linearGradient id="walletGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="depositGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }}
                  tickFormatter={v => v?.slice(5, 10)} axisLine={{ stroke: '#1e293b' }} tickLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }}
                  tickFormatter={v => `$${(v/1000).toFixed(0)}K`} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: '#11141D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 0, fontFamily: 'monospace', fontSize: 12 }}
                  labelStyle={{ color: '#94a3b8' }}
                  formatter={(v, name) => [`$${v?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, name === 'balance' ? 'Balance' : 'Deposited']}
                />
                <Area type="monotone" dataKey="total_deposited" stroke="#3b82f6" fill="url(#depositGrad)" strokeWidth={1.5} dot={false} />
                <Area type="monotone" dataKey="balance" stroke="#10b981" fill="url(#walletGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Agent Performance Breakdown */}
      {portfolio.agents?.length > 0 && (
        <div className="bg-[#090B0F] border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-500" /> Performance by Agent
            </h3>
            <span className="text-xs font-mono text-slate-400">{portfolio.num_agents} active agents</span>
          </div>
          <div className="p-4 h-[200px]" data-testid="agent-performance-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={portfolio.agents} layout="vertical" margin={{ left: 60 }}>
                <XAxis type="number" tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }}
                  tickFormatter={v => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="agent_id" tick={{ fill: '#e2e8f0', fontSize: 11, fontFamily: 'monospace' }}
                  axisLine={false} tickLine={false} width={55} />
                <Tooltip
                  contentStyle={{ background: '#11141D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 0, fontFamily: 'monospace', fontSize: 12 }}
                  formatter={(v) => [`${v >= 0 ? '+' : ''}${v.toFixed(2)}%`, 'Return']}
                />
                <Bar dataKey="return_pct" radius={[0, 4, 4, 0]}>
                  {portfolio.agents.map((entry, idx) => (
                    <Cell key={idx} fill={entry.return_pct >= 0 ? '#10b981' : '#ef4444'} fillOpacity={0.8} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-t border-b border-white/10 text-slate-500 text-xs font-mono uppercase">
                  <th className="text-left p-3">{t('common.agent')}</th>
                  <th className="text-right p-3">{t('common.invested')}</th>
                  <th className="text-right p-3">{t('wallet.currentValue')}</th>
                  <th className="text-right p-3">P&L</th>
                  <th className="text-right p-3">{t('common.return')}</th>
                </tr>
              </thead>
              <tbody>
                {portfolio.agents.map(a => (
                  <tr key={a.agent_id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 font-mono text-white font-bold">{a.agent_id} <span className="text-slate-500 text-xs font-normal">{a.agent_name}</span></td>
                    <td className="p-3 text-right text-white">${a.invested?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="p-3 text-right text-white">${a.current_value?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className={`p-3 text-right font-mono font-bold ${a.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {a.pnl >= 0 ? '+' : ''}${a.pnl?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className={`p-3 text-right font-mono ${a.return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {a.return_pct >= 0 ? '+' : ''}{a.return_pct?.toFixed(2)}%
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-white/20 bg-white/5 font-bold">
                  <td className="p-3 text-white">{t('wallet.total')}</td>
                  <td className="p-3 text-right text-white">${portfolio.total_invested_in_agents?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right text-white">${portfolio.portfolio_value?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className={`p-3 text-right font-mono ${portfolio.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {portfolio.total_pnl >= 0 ? '+' : ''}${portfolio.total_pnl?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className={`p-3 text-right font-mono ${portfolio.total_return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {portfolio.total_return_pct >= 0 ? '+' : ''}{portfolio.total_return_pct?.toFixed(2)}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(!portfolio.agents || portfolio.agents.length === 0) && (
        <div className="bg-[#090B0F] border border-white/10 p-8 text-center">
          <WalletIcon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-white font-bold mb-2">{t('wallet.noActiveInvestments')}</h3>
          <p className="text-slate-500 text-sm mb-4">{t('wallet.noInvestmentsDesc')}</p>
          <button onClick={() => onSwitchTab('overview')} className="px-6 py-2 bg-emerald-500 text-[#030508] font-bold text-sm hover:bg-emerald-400 transition-colors" data-testid="portfolio-deposit-cta">
            Deposit Funds
          </button>
        </div>
      )}
    </div>
  );
}
